import { useDepot } from '#~/depot/hooks.js';
import { useRoom } from '#~/room/hooks.js';
import { useDialog } from '@studio/design-system/dialog';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { UserInviteDialogProps } from './dialog.jsx';
import { UserInviteDialog } from './dialog.jsx';

/**
 * Gets the auto tracking protection state for a specific camera.
 *
 * @returns The auto tracking protection state for the camera.
 */
export const useUserInvitePrompt = (
  unsafeRoomId: T_Maybe<Room['id']>
): {
  open: () => void;
  canCreateInvite: boolean;
} => {
  const dialog = useDialog<UserInviteDialogProps>(UserInviteDialog);

  const roomId = useRoom((µ) => unsafeRoomId ?? µ.id);
  const [organizationId, canCreateInvite] = useDepot((u) => [
    u.roomRecords[roomId]?.organizationId,
    u.roomRecords[roomId]?.permissions.canCreateInvite
  ]);

  const open = (): void => {
    if (!roomId || !organizationId) {
      return;
    }

    const onClose = (): void => {
      dialog.close();
    };

    dialog.open({
      roomId,
      organizationId,
      onClose
    });
  };

  return {
    open,
    canCreateInvite: !!canCreateInvite
  };
};
